import { useEffect, useState } from 'react';
import type {
  FlowGraphEdge,
  ModuleExit,
  ModuleNodeData,
  ModuleParameter,
} from '../types';
import { ParamEditor, defaultKindFor, type ParamKind } from './ParamEditor';
import { isExitActive } from '../exitVisibility';

/**
 * Spoken-text parameters on Mod_VB_* nodes. Migration 026 retypes these to
 * the PromptText parameter type; until it has run (or on older catalogs)
 * we still want the multiline ${VAR} editor for them, so match by name.
 */
const VOICEBOT_PROMPT_PARAMS = new Set(['text', 'prompttext', 'processingtext']);
function isVoicebotPromptParam(scriptName: string | null | undefined, paramName: string): boolean {
  if (!(scriptName ?? '').startsWith('Mod_VB_')) return false;
  return VOICEBOT_PROMPT_PARAMS.has(paramName.trim().toLowerCase());
}

export interface SelectedEdgeInfo {
  edgeId: string;
  fromNodeId: number;
  fromNodeName: string;
  fromModuleName: string | null;
  exitId: number;
  exitName: string;
  toNodeId: number;
  toNodeName: string;
}

interface Props {
  selected: ModuleNodeData | null;
  selectedEdge: SelectedEdgeInfo | null;
  edges: FlowGraphEdge[];
  nodeNameById: Map<number, string>;
  applicationId: number;
  readOnly: boolean;
  isInitialNode: boolean;
  onChangeName: (name: string) => void;
  onChangeParam: (param: ModuleParameter, value: string, kind: string) => void;
  onDelete: () => void;
  onSetInitial: () => void;
  // Flip a single exit's isActive flag on the selected node. The server
  // persists the change immediately; the caller is expected to patch local
  // node data.
  onSetExitActive: (exit: ModuleExit, isActive: boolean, isConnected: boolean) => void;
}

// Renders a dynamic form based on the module's ModuleParameter[]. The mapping
// from TypeId to UI control mirrors the legacy NodeDetails.cshtml designer;
// each editor supports switching between its native kind (string/int/huntgroup/…)
// and a Variable reference where applicable. Values on the wire are
// "<kind>:<value>" so we keep the kind in ValueType.
export function PropertiesPanel({
  selected,
  selectedEdge,
  edges,
  nodeNameById,
  applicationId,
  readOnly,
  isInitialNode,
  onChangeName,
  onChangeParam,
  onDelete,
  onSetInitial,
  onSetExitActive,
}: Props) {
  const def = selected?.moduleDef;

  // When an edge is selected (and no node), render the connection details
  // and a delete button instead of the node configuration form.
  if (selectedEdge && !selected) {
    return <EdgeProperties info={selectedEdge} readOnly={readOnly} onDelete={onDelete} />;
  }

  if (!selected) {
    return (
      <div className="fb-props">
        <div className="fb-props-hd">
          <span className="fb-props-title">Properties</span>
        </div>
        <div className="fb-props-empty">
          Select a node or connection to view details<br />
          <small>Drag from the palette to add nodes · press DEL to remove</small>
        </div>
      </div>
    );
  }

  // Order parameters by their ModuleParameter.sortOrder so the form mirrors
  // the legacy NodeDetails layout. Nulls sink to the bottom; parameterId is
  // the stable tiebreaker so equal sort orders render in a deterministic
  // (insertion) order. We make a defensive copy to avoid mutating the
  // catalog's array.
  const params = (def?.parameters ?? []).slice().sort((a, b) => {
    const av = a.sortOrder ?? Number.POSITIVE_INFINITY;
    const bv = b.sortOrder ?? Number.POSITIVE_INFINITY;
    if (av !== bv) return av - bv;
    return a.parameterId - b.parameterId;
  });
  const exits = def?.exits ?? [];

  // ModuleParameter rows with TypeId === 14 are not data — they're visual
  // section breaks in the configuration form. We render them as a styled
  // header (portal green) instead of an editor.
  const isSectionDivider = (typeId: number) => typeId === 14;

  // Split exits into "active" (shown on the node) and "not used" (hidden,
  // offered with an Activate button). Connected exits are forced into
  // "active" because an edge wouldn't render otherwise.
  const exitRows = exits.map((exit) => {
    const edge = edges.find(
      (e) => e.fromNodeId === selected.nodeId && e.exitId === exit.exitId
    );
    const stored = selected.exitStates?.[exit.exitId];
    const active = !!edge || isExitActive(exit, stored);
    return { exit, edge, active };
  });

  return (
    <div className={`fb-props${readOnly ? ' fb-props--readonly' : ''}`}>
      <div className="fb-props-hd">
        <span className="fb-props-title">{selected.label || def?.name || 'Node'}</span>
        <span className="fb-props-badge">{def?.name || `module ${selected.moduleId}`}</span>
      </div>

      <div className="fb-props-body">
        <NameField initial={selected.label} onChange={onChangeName} readOnly={readOnly} />

        <div className="fb-divider" />

        {params.filter((p) => !isSectionDivider(p.typeId)).length === 0 ? (
          <div className="fb-muted">This module has no parameters.</div>
        ) : (
          (() => {
            // Build a snapshot of every parameter's current value keyed by
            // its (lowercased) typeName. Editors like Voice preset use
            // this to look up the sibling Language value on the same
            // node so they can filter their list accordingly. We compute
            // it once per render — params here is small (a handful) so
            // the cost is negligible.
            const siblingByTypeName: Record<string, string | null | undefined> = {};
            for (const sp of params) {
              const key = (sp.typeName ?? '').trim().toLowerCase();
              if (!key) continue;
              const storedSib = selected.parameters[sp.parameterId];
              const v = storedSib?.value ?? sp.defaultValue ?? '';
              // Keep the first non-empty value when two siblings share a
              // typeName (highly unusual but defensive).
              if (!siblingByTypeName[key] && v) siblingByTypeName[key] = v;
            }
            return params.map((p) => {
              if (isSectionDivider(p.typeId)) {
                return <SectionHeader key={p.parameterId} title={p.name} subtitle={p.description} />;
              }
              const stored = selected.parameters[p.parameterId];
              const rawValue = stored?.value ?? p.defaultValue ?? '';
              // Some types are not promotable (KVP, hidden, bit, dtmf, date,
              // time, simpledb, variablelist) — always pick the editor that
              // matches the parameter type definition. For promotable types we
              // honour the stored valueType so a "Variable" override sticks.
              const isPromotable = [1, 2, 3, 4, 9, 10, 11, 12].includes(p.typeId);
              const storedKind = isPromotable
                ? (stored?.valueType as ParamKind | null | undefined)
                : null;
              let rawKind = storedKind || defaultKindFor(p.typeId, p.typeName);
              // Spoken-text fields on voicebot nodes get the multiline prompt
              // editor even before migration 026 retypes them to PromptText
              // in the DB; the stored valueType stays 'string'.
              if (rawKind === 'string' && isVoicebotPromptParam(def?.scriptName, p.name)) {
                rawKind = 'prompttext';
              }
              return (
                <ParamField
                  key={p.parameterId}
                  param={p}
                  value={rawValue}
                  kind={rawKind}
                  applicationId={applicationId}
                  readOnly={readOnly}
                  onChange={({ value, kind }) => onChangeParam(p, value, kind)}
                  siblingValues={siblingByTypeName}
                />
              );
            });
          })()
        )}

        {exits.length > 0 && (
          <>
            <div className="fb-divider" />
            <div className="fb-section">Output ports</div>
            <div>
              {exitRows
                .filter((r) => r.active)
                .map(({ exit, edge }) => {
                  const target = edge ? nodeNameById.get(edge.toNodeId) : null;
                  return (
                    <div key={exit.exitId} className="fb-port-row">
                      <span className="fb-port-name">{exit.name}</span>
                      {edge ? (
                        <span
                          className="fb-port-status fb-port-status--ok"
                          title={`Connected to node ${edge.toNodeId}`}
                        >
                          → {target || `node ${edge.toNodeId}`}
                        </span>
                      ) : (
                        <span className="fb-port-status">unconnected</span>
                      )}
                      {!readOnly && (
                        <button
                          type="button"
                          className="fb-btn fb-btn--mini"
                          onClick={() => onSetExitActive(exit, false, !!edge)}
                          title={
                            edge
                              ? 'Hide this exit. The existing connection will be kept in the database but not rendered.'
                              : 'Hide this exit from the node.'
                          }
                        >
                          Not used
                        </button>
                      )}
                    </div>
                  );
                })}
            </div>

            {(() => {
              const inactive = exitRows.filter((r) => !r.active);
              if (inactive.length === 0) return null;
              return (
                <>
                  <div className="fb-subsection">Not used</div>
                  <div>
                    {inactive.map(({ exit }) => (
                      <div key={exit.exitId} className="fb-port-row fb-port-row--inactive">
                        <span className="fb-port-name fb-muted">{exit.name}</span>
                        <span className="fb-port-status">hidden</span>
                        {!readOnly && (
                          <button
                            type="button"
                            className="fb-btn fb-btn--mini"
                            onClick={() => onSetExitActive(exit, true, false)}
                            title="Show this exit as a handle on the node"
                          >
                            Activate
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </>
              );
            })()}

          </>
        )}

        <div className="fb-props-actions">
          <button
            className="fb-btn"
            onClick={onSetInitial}
            disabled={isInitialNode || readOnly}
            title="Mark as the start of this flow"
          >
            {isInitialNode ? 'Start' : 'Set as start'}
          </button>
          <button className="fb-btn fb-btn--danger" onClick={onDelete} disabled={readOnly}>
            Delete
          </button>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string | null;
}) {
  return (
    <div className="fb-param-section">
      <div className="fb-param-section-title">{title || 'Section'}</div>
      {subtitle && <div className="fb-param-section-sub">{subtitle}</div>}
    </div>
  );
}

function NameField({
  initial,
  onChange,
  readOnly,
}: {
  initial: string;
  onChange: (v: string) => void;
  readOnly: boolean;
}) {
  const [value, setValue] = useState(initial);
  useEffect(() => setValue(initial), [initial]);
  return (
    <div className="fb-field">
      <label className="fb-lbl">Node label</label>
      <input
        className="fb-input"
        value={value}
        readOnly={readOnly}
        onChange={(e) => setValue(e.target.value)}
        onBlur={() => {
          if (!readOnly && value !== initial) onChange(value);
        }}
      />
    </div>
  );
}

interface ParamFieldProps {
  param: ModuleParameter;
  value: string;
  kind: ParamKind;
  applicationId: number;
  readOnly: boolean;
  onChange: (next: { value: string; kind: ParamKind }) => void;
  siblingValues?: Record<string, string | null | undefined>;
}

function EdgeProperties({
  info,
  readOnly,
  onDelete,
}: {
  info: SelectedEdgeInfo;
  readOnly: boolean;
  onDelete: () => void;
}) {
  return (
    <div className={`fb-props${readOnly ? ' fb-props--readonly' : ''}`}>
      <div className="fb-props-hd">
        <span className="fb-props-title">Connection</span>
        <span className="fb-props-badge">edge</span>
      </div>
      <div className="fb-props-body">
        <div className="fb-section">From</div>
        <div className="fb-edge-endpoint">
          <div className="fb-edge-node">{info.fromNodeName}</div>
          {info.fromModuleName && (
            <div className="fb-edge-sub">{info.fromModuleName}</div>
          )}
          <div className="fb-edge-sub">
            exit: <span className="fb-edge-exit">{info.exitName}</span>
          </div>
        </div>

        <div className="fb-edge-arrow">↓</div>

        <div className="fb-section">To</div>
        <div className="fb-edge-endpoint">
          <div className="fb-edge-node">{info.toNodeName}</div>
          <div className="fb-edge-sub">input</div>
        </div>

        <div className="fb-help" style={{ marginTop: 12 }}>
          Press <kbd>DEL</kbd> or use the button below to remove this connection.
          A node's exit can only have one target — reconnect by dragging from
          the exit handle.
        </div>
        <div className="fb-props-actions">
          <button className="fb-btn fb-btn--danger" onClick={onDelete} disabled={readOnly}>
            Delete connection
          </button>
        </div>
      </div>
    </div>
  );
}

function ParamField({ param, value, kind, applicationId, readOnly, onChange, siblingValues }: ParamFieldProps) {
  return (
    <div className="fb-field">
      <label className="fb-lbl">
        {param.name}
        {!param.allowNull && <span className="fb-req">*</span>}
      </label>
      <ParamEditor
        param={param}
        value={value ?? ''}
        kind={kind}
        applicationId={applicationId}
        siblingValues={siblingValues}
        readOnly={readOnly}
        onChange={onChange}
      />
      {param.description && <div className="fb-help">{param.description}</div>}
    </div>
  );
}
